import { Button, Empty, Input, LoadingSpinner, mapDataToSuggestions, SmartDropdown, SmartDropdownOption, SuggestionMapParams } from "@xapp/components";
import { Template, TemplateType } from "@xapp/ovai-lib";
import { RadioIcon, RadioIconGroup } from "components/StentorUIComponents";
import { Organization, SearchedOrg } from "generated/graphql";
import { ClientApp } from "models";
import React, { Component } from "react";
import forms from "styles/forms.scss";
import { Noop } from "utils/Noop";
import { idify } from "utils/String";
import { CUSTOM_TYPE, ON_DEMAND_TEMPLATE, RADIO_TEMPLATE } from "constants/templates";

export interface Field {
    disabled?: boolean;
    value?: string;
    show?: boolean;
}

export interface TemplateField extends Field {
    value?: TemplateType | undefined;
}

export interface FormFields {
    appId?: Field;
    organizationId?: Field;
    templateType?: TemplateField;
}

export interface NewAppFormProps {
    defaultFieldValues?: FormFields;
    availableTemplates: (TemplateType | string)[];
    isBusy?: boolean;
    organizations: Pick<Organization, "organizationId" | "name">[];
    onSave(app: ClientApp | Template): void;
    onCancel(): void;
}

export interface NewAppFormState {
    app: ClientApp;
    formIsValid: boolean;
    isBusy: boolean;
    organizations: Pick<Organization, "organizationId" | "name">[];
    slugLabel: string;
}

export class NewAppForm extends Component<NewAppFormProps, NewAppFormState> {
    static defaultProps: NewAppFormProps = {
        availableTemplates: ["CUSTOM"],
        defaultFieldValues: {
            appId: {
                disabled: false,
                show: false,
                value: undefined
            },
            organizationId: {
                disabled: false,
                show: false,
                value: undefined
            },
            templateType: {
                disabled: false,
                show: true,
                value: undefined
            }
        },
        onCancel: Noop,
        onSave: Noop,
        organizations: []
    };

    constructor(props: NewAppFormProps) {
        super(props);

        const { defaultFieldValues } = props;
        const { appId, organizationId, templateType } = defaultFieldValues;

        this.state = {
            app: {
                appId: appId ? appId.value : "",
                name: "",
                organizationId: organizationId ? organizationId.value : "",
                templateType: templateType ? templateType.value : undefined
            } as ClientApp,
            formIsValid: false,
            isBusy: false,
            organizations: props.organizations,
            slugLabel: ""
        };

        this.onSave = this.onSave.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.handleOrgChange = this.handleOrgChange.bind(this);
    }

    componentWillReceiveProps(newProps: NewAppFormProps) {
        this.setState({
            isBusy: newProps.isBusy,
            organizations: newProps.organizations
        });
    }

    getOrgNameFromID(orgId: string): string {
        const selectedOrg = this.state.organizations.find((org: SearchedOrg) => org.organizationId === orgId);

        return selectedOrg.name;
    }

    handleChange(key: string, value: string) {

        let app = { ...this.state.app, [key]: value };

        // idify the appId as a suggestion to the user
        if (key === "name") {
            const appId = idify(value);
            app = { ...app, appId };
        }

        this.setState({ app }, this.validateForm);
    }

    handleOrgChange(suggestion: SmartDropdownOption) {
        this.setState({
            app: {
                ...this.state.app,
                organizationId: suggestion.id
            },
            slugLabel: this.getOrgNameFromID(suggestion.id)
        });
    }

    onSave() {
        this.props.onSave(this.state.app);
    }

    validateForm() {
        const { name, templateType } = this.state.app;
        const { defaultFieldValues } = this.props;
        let formIsValid = false;

        if (name !== "" && (defaultFieldValues.templateType.show ? templateType !== undefined : true)) {
            formIsValid = true;
        }

        this.setState({ formIsValid });
    }

    renderTypeFields(show: boolean) {
        const { availableTemplates } = this.props;
        const { templateType } = this.state.app;

        if (show) {

            const icons: JSX.Element[] = [];

            availableTemplates.forEach((type, index) => {
                switch (type) {
                    case RADIO_TEMPLATE:
                        icons.push(
                            <RadioIcon
                                key={`${type}-${index}`}
                                name="templateType"
                                label="Radio"
                                value={RADIO_TEMPLATE}
                                icon="radio"
                            />
                        );
                        break;
                    case ON_DEMAND_TEMPLATE:
                        icons.push(
                            <RadioIcon
                                key={`${type}-${index}`}
                                name="templateType"
                                label="Podcast"
                                value={ON_DEMAND_TEMPLATE}
                                icon="rss_feed"
                            />
                        );
                        break;
                    case "OC_STUDIO_STARTER_TEMPLATE":
                        icons.push(
                            <RadioIcon
                                key={`${type}-${index}`}
                                name="templateType"
                                label="CSS4CCI"
                                value={"OC_STUDIO_STARTER_TEMPLATE"}
                                icon="help_outline"
                            />
                        );
                        break;
                    case "CUSTOM":
                    default:
                        icons.push(
                            <RadioIcon
                                key={`${type}-${index}`}
                                name="templateType"
                                label="Custom"
                                value={CUSTOM_TYPE}
                                icon="build"
                            />
                        );
                }
            });

            return (
                <div className={forms.fieldset}>
                    <h3>What type of app?</h3>

                    <RadioIconGroup
                        ariaLabel="Select App Type"
                        onChange={this.handleChange}
                        selectedOption={templateType}
                    >
                        {icons}
                    </RadioIconGroup>
                    {availableTemplates.includes("CUSTOM") ? <p>Select Custom to start from scratch</p> : <></>}
                </div>
            );
        }
    }

    render() {
        const { defaultFieldValues } = this.props;
        const { appId, organizationId, templateType } = defaultFieldValues;

        if (this.state.isBusy) {
            return this.renderBusyState();
        }

        return (
            <section>
                <div>
                    {organizationId.show && this.renderOrgSelector()}

                    <div className={forms.fieldset}>
                        <h3>What is the name of your app?</h3>

                        <Input
                            data-puppet="create-app-input_name"
                            type="text"
                            label="Name"
                            value={this.state.app.name}
                            onChange={this.handleChange.bind(this, "name")}
                            variation="standard"
                        />
                    </div>

                    {appId.show && (
                        <Input
                            type="text"
                            label="App ID"
                            value={this.state.app.appId}
                            disabled={appId.disabled}
                            onChange={this.handleChange.bind(this, "appId")}
                            variation="standard"
                        />
                    )}

                    {this.renderTypeFields(templateType.show)}
                </div>

                <footer className={forms.footer}>
                    <Button variation="flat" label="Cancel" onClick={this.props.onCancel} />
                    <Button variation="primary" label="Create App" disabled={!this.state.formIsValid} onClick={this.onSave} />
                </footer>
            </section>
        );
    }

    renderBusyState() {
        return (
            <Empty title="Hold Tight. We are creating your new App">
                <LoadingSpinner />
            </Empty>
        );
    }

    renderOrgSelector() {
        const suggestionMapParams: SuggestionMapParams = {
            dataSet: this.state.organizations,
            idField: "organizationId",
            nameField: "name"
        };
        const dropdownOptions = mapDataToSuggestions(suggestionMapParams);

        return (
            <div className={forms.fieldset}>
                <h3>Which Organization should this App be added to?</h3>

                <SmartDropdown
                    options={dropdownOptions}
                    onChange={this.handleOrgChange}
                    slugLabel={this.state.slugLabel}
                />
            </div>
        );
    }
}
